import { useEffect, useState } from "react";
import { Heart, MessageCircle, Send, QrCode, Image as ImageIcon, Zap } from "lucide-react";
import { toast } from "sonner";
import FavoriteNotifier from "./FavoriteNotifier";
import FavoritesQRModal from "./FavoritesQRModal";
import SharePreviewModal from "./SharePreviewModal";
import { getSongsCached, findSongCode } from "../lib/songMatch";
import { generateFavoritesCard } from "../lib/shareCard";

const STORAGE_KEY = "rrz-favorites";
const AUTOPLAY_KEY = "rrz-autoplay-on-fav";
const WHATSAPP_NUMBER = "393917041816";

function parseRawTrack(raw) {
  const s = (raw || "").trim();
  if (s.includes(" - ")) {
    const [left, ...rest] = s.split(" - ");
    const right = rest.join(" - ").trim();
    if (left && right) {
      return left.length <= right.length
        ? { artist: left.trim(), title: right }
        : { artist: right, title: left.trim() };
    }
  }
  return { artist: "", title: s };
}

export function loadFavorites() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

export function saveFavorites(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent("rrz:favorites-changed"));
}

export function isFavorite(rawTitle) {
  if (!rawTitle) return false;
  const key = rawTitle.toLowerCase();
  return loadFavorites().some((f) => f.raw?.toLowerCase() === key);
}

export function toggleFavorite(rawTitle, extra = {}) {
  const raw = (rawTitle || "").trim();
  if (!raw) return false;
  const key = raw.toLowerCase();
  const list = loadFavorites();
  const idx = list.findIndex((f) => f.raw?.toLowerCase() === key);
  if (idx >= 0) {
    list.splice(idx, 1);
    saveFavorites(list);
    return false;
  }
  list.unshift({ raw, addedAt: Date.now(), ...extra });
  saveFavorites(list.slice(0, 100));
  return true;
}

function parseTrack(raw) {
  return parseRawTrack(raw);
}

export default function Favorites() {
  const [favs, setFavs] = useState(() => loadFavorites());
  const [qrOpen, setQrOpen] = useState(false);
  // Share-image modal state
  const [sharePreview, setSharePreview] = useState({ open: false, blob: null, text: "", url: "" });
  // Auto-play when a favorite comes on-air
  const [autoplay, setAutoplay] = useState(() => {
    try { return localStorage.getItem(AUTOPLAY_KEY) === "1"; } catch { return false; }
  });
  // Track title currently on-air so we can highlight the matching favorite
  const [onAirKey, setOnAirKey] = useState("");

  useEffect(() => {
    const onNow = (ev) => {
      const t = (ev?.detail?.title || "").trim().toLowerCase();
      setOnAirKey(t);
    };
    window.addEventListener("rrz:now-playing", onNow);
    return () => window.removeEventListener("rrz:now-playing", onNow);
  }, []);

  useEffect(() => {
    const refresh = () => setFavs(loadFavorites());
    window.addEventListener("rrz:favorites-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("rrz:favorites-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  // Backfill missing codes on mount for favorites saved before this feature.
  useEffect(() => {
    const missing = favs.filter((f) => !f.code);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const songs = await getSongsCached();
      if (cancelled || songs.length === 0) return;
      const updated = loadFavorites().map((f) => {
        if (f.code) return f;
        const m = findSongCode(f.raw, songs);
        return m ? { ...f, code: m.code } : f;
      });
      // Only persist if anything changed
      const changed = updated.some((f, i) => f.code !== favs[i]?.code);
      if (changed) saveFavorites(updated);
    })();
    return () => { cancelled = true; };
  }, [favs]);

  const remove = (raw) => {
    toggleFavorite(raw);
    toast.success("Rimosso dai preferiti");
  };

  const askRemove = (idx) => {
    const fav = favs[idx];
    if (!fav) return;
    const ok = window.confirm(`Rimuovere "${fav.raw}" dai preferiti?`);
    if (ok) remove(fav.raw);
  };

  const requestOnWhatsApp = async (fav) => {
    // Prefer the code stored on the favorite (looked up when saved).
    let code = fav.code || null;
    if (!code) {
      const loadingId = toast.loading("Cerco il codice del brano…");
      const songs = await getSongsCached();
      const match = findSongCode(fav.raw, songs);
      toast.dismiss(loadingId);
      if (!match) {
        toast.error("Codice non trovato", {
          description: "Questo brano non risulta nella lista richieste. Prova dalla sezione Richieste.",
        });
        return;
      }
      code = match.code;
    }
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`*${code}`)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const sendListOnWhatsApp = () => {
    if (favs.length === 0) return;
    const lines = favs.map((f, i) => `${i + 1}. ${f.raw}`).join("\n");
    const msg = `🎵 I miei preferiti su Radio Rumore Zero:\n\n${lines}\n\nAscolta anche tu: ${(typeof window !== "undefined" && window.location?.origin) || "https://www.rumorezero.com"}`;
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareAsImage = async () => {
    if (favs.length === 0) return;
    const url = (typeof window !== "undefined" && window.location?.origin) || "https://www.rumorezero.com";
    const text = `🎵 La mia playlist del cuore su Radio Rumore Zero — ${favs.length} brani 60·70·80·90.`;
    // Open modal immediately with placeholder while canvas renders
    setSharePreview({ open: true, blob: null, text, url });
    try {
      const blob = await generateFavoritesCard({ favorites: favs });
      setSharePreview({ open: true, blob, text, url });
    } catch {
      setSharePreview({ open: true, blob: null, text, url });
      toast.error("Impossibile generare l'immagine");
    }
  };

  const toggleAutoplay = () => {
    const next = !autoplay;
    setAutoplay(next);
    try { localStorage.setItem(AUTOPLAY_KEY, next ? "1" : "0"); } catch {}
    window.dispatchEvent(new CustomEvent("rrz:autoplay-fav-changed", { detail: { enabled: next } }));
    if (next) {
      toast.success("Avvio automatico attivo", {
        description: "Partirà la radio quando passa un tuo preferito.",
      });
    } else {
      toast.info("Avvio automatico disattivato");
    }
  };

  if (favs.length === 0) return null;

  return (
    <section
      id="preferiti"
      data-testid="favorites-section"
      className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14 border-b border-[#332f2b]"
    >
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 min-w-0 sm:flex-1">
          <span className="shrink-0 w-10 h-10 rounded-lg bg-[#FF6B00]/10 border border-[#FF6B00]/40 flex items-center justify-center text-[#FF6B00]">
            <Heart size={18} fill="currentColor" />
          </span>
          <div className="min-w-0">
            <h2 className="font-anton uppercase text-2xl sm:text-3xl text-[#F5F2EB] tracking-wide leading-none">
              I tuoi preferiti
            </h2>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#69635c] mt-1">
              {favs.length} {favs.length === 1 ? "brano salvato" : "brani salvati"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            data-testid="favorites-autoplay-toggle"
            onClick={toggleAutoplay}
            aria-pressed={autoplay}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition font-mono text-[10px] uppercase tracking-[0.18em] ${
              autoplay
                ? "bg-[#FF6B00]/15 border-[#FF6B00] text-[#FF6B00] shadow-[0_0_14px_rgba(255,107,0,0.35)]"
                : "border-[#FF6B00]/40 bg-[#FF6B00]/5 text-[#A39A8F] hover:text-[#FF6B00] hover:border-[#FF6B00]/60"
            }`}
            aria-label="Avvio automatico quando passa un preferito"
            title="Avvia la radio quando passa un tuo preferito"
          >
            <Zap size={14} fill={autoplay ? "currentColor" : "none"} />
            <span className="hidden sm:inline">
              {autoplay ? "Auto-play ON" : "Auto-play"}
            </span>
          </button>
          <button
            type="button"
            data-testid="favorites-share-image"
            onClick={shareAsImage}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#1E88E5]/40 bg-[#1E88E5]/10 text-[#1E88E5] hover:bg-[#1E88E5]/20 transition font-mono text-[10px] uppercase tracking-[0.18em]"
            aria-label="Condividi i preferiti come immagine"
            title="Esporta come immagine 1080×1080 per Instagram"
          >
            <ImageIcon size={14} />
            <span className="hidden sm:inline">Immagine</span>
          </button>
          <button
            type="button"
            data-testid="favorites-qr-open"
            onClick={() => setQrOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#FF6B00]/40 bg-[#FF6B00]/10 text-[#FF6B00] hover:bg-[#FF6B00]/20 transition font-mono text-[10px] uppercase tracking-[0.18em]"
            aria-label="Condividi via QR code"
            title="Porta i preferiti su un altro dispositivo"
          >
            <QrCode size={14} />
            <span className="hidden sm:inline">QR</span>
          </button>
          <button
            type="button"
            data-testid="favorites-send-list"
            onClick={sendListOnWhatsApp}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#25D366]/40 bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 transition font-mono text-[10px] uppercase tracking-[0.18em]"
            aria-label="Invia la lista via WhatsApp"
          >
            <Send size={14} />
            <span className="hidden sm:inline">Invia lista</span>
          </button>
          <FavoriteNotifier />
        </div>
      </div>

      <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {favs.map((f, i) => {
          const { artist, title } = parseTrack(f.raw);
          const isOnAir = !!onAirKey && f.raw?.toLowerCase() === onAirKey;
          return (
            <li
              key={`${f.addedAt}-${i}`}
              data-testid={`favorite-item-${i}`}
              data-onair={isOnAir ? "true" : undefined}
              className={`flex items-center gap-3 rounded-xl p-3 transition-all duration-300 ${
                isOnAir
                  ? "border-2 border-[#FF6B00] bg-[#FF6B00]/10 shadow-[0_0_28px_rgba(255,107,0,0.35)] fav-onair-pulse"
                  : "border border-[#332f2b] bg-[#1a1816] hover:border-[#FF6B00]/40"
              }`}
            >
              <button
                type="button"
                data-testid={`favorite-heart-remove-${i}`}
                onClick={() => askRemove(i)}
                aria-label="Rimuovi dai preferiti"
                title="Clicca per rimuovere dai preferiti"
                className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-[#FF6B00] transition active:scale-90 hover:bg-[#ff5252]/15 hover:text-[#ff5252] group ${
                  isOnAir
                    ? "bg-[#FF6B00]/20 border border-[#FF6B00]"
                    : "bg-[#12100e] border border-[#332f2b] hover:border-[#ff5252]/50"
                }`}
              >
                <Heart size={16} fill="currentColor" className="group-hover:scale-110 transition-transform" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className={`font-oswald uppercase text-sm leading-tight truncate ${isOnAir ? "text-[#FF6B00]" : "text-[#F5F2EB]"}`}>
                    {title || f.raw}
                  </p>
                  {isOnAir && (
                    <span
                      data-testid={`favorite-onair-badge-${i}`}
                      className="shrink-0 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-[#FF6B00] text-[#12100e] font-bold"
                      aria-label="Attualmente in onda"
                    >
                      <span className="relative inline-flex w-1.5 h-1.5">
                        <span className="absolute inline-flex w-full h-full rounded-full bg-[#12100e] opacity-70 animate-ping" />
                        <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-[#12100e]" />
                      </span>
                      On Air
                    </span>
                  )}
                  {f.code && !isOnAir && (
                    <span
                      data-testid={`favorite-code-${i}`}
                      className="shrink-0 font-mono text-[10px] px-1.5 py-0.5 rounded bg-[#FF6B00]/10 text-[#FF6B00] border border-[#FF6B00]/30 tabular-nums"
                      title="Codice per la richiesta via WhatsApp"
                    >
                      *{f.code}
                    </span>
                  )}
                </div>
                {artist && (
                  <p className={`font-mono text-[10px] uppercase tracking-[0.18em] truncate mt-0.5 ${isOnAir ? "text-[#E1A140]" : "text-[#A39A8F]"}`}>
                    {artist}
                  </p>
                )}
              </div>
              <button
                type="button"
                data-testid={`favorite-request-${i}`}
                onClick={() => requestOnWhatsApp(f)}
                aria-label="Richiedi su WhatsApp"
                className="shrink-0 p-2 rounded-md border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 transition"
              >
                <MessageCircle size={16} />
              </button>
            </li>
          );
        })}
      </ul>

      <FavoritesQRModal open={qrOpen} favs={favs} onClose={() => setQrOpen(false)} />
      <SharePreviewModal
        open={sharePreview.open}
        blob={sharePreview.blob}
        text={sharePreview.text}
        url={sharePreview.url}
        onClose={() => setSharePreview((s) => ({ ...s, open: false }))}
      />
    </section>
  );
}
